import React, { useEffect, useState } from "react";
import AdminHeader from "../../component/AdminHeader";
import StatCard from "../../component/StatCard";
import { ChevronDown, Package } from "lucide-react";
import { http } from "../../axios/axios";
import { GetImageUrl } from "../../helpers/GetImageUrl";

const AdminReviews = () => {
  const [allReviews, setAllReviews] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [openMessageId, setOpenMessageId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const fetchReviews = async () => {
    try {
      const res = await http.get("reviews");
      setAllReviews(res.data.data);
      setReviewCount(res.data.count);
    } catch (error) {
      console.error(error);
    }
  };
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this review?",
    );

    if (!confirmDelete) return;

    try {
      setLoadingId(id);

      await http.delete(`reviews/${id}`);

      if (openMessageId === id) {
        setOpenMessageId(null);
      }

      await fetchReviews();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingId(null);
    }
  };
  const handleResolve = async (id) => {
    try {
      setLoadingId(id);

      await http.patch(`reviews/${id}`, {
        status: "approved",
      });

      await fetchReviews();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingId(null);
    }
  };

  const handleFeatured = async (id, featured) => {
    try {
      setLoadingId(id);

      await http.patch(`reviews/${id}`, {
        featured,
      });

      await fetchReviews();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingId(null);
    }
  };

  const options = [
    { label: "⭐⭐⭐⭐⭐", value: "5" },
    { label: "⭐⭐⭐⭐", value: "4" },
    { label: "⭐⭐⭐", value: "3" },
    { label: "⭐⭐", value: "2" },
    { label: "⭐", value: "1" },
  ];

  useEffect(() => {
    fetchReviews();
  }, []);

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader title="Manage Reviews" />
      <div className="flex flex-col md:flex-row w-full mb-8 gap-4">
        <StatCard title={"Total Reviews"} icon={Package} count={reviewCount} />
      </div>
      <div className="space-y-4">
        {allReviews?.length === 0 ? (
          <div className="p-8 rounded bg-white/5 border border-white/10 text-center text-slate-400">
            No Review Found
          </div>
        ) : (
          allReviews.map((review) => (
            <div
              key={review?._id}
              className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 hover:border-slate-700"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                {/* Left Section */}
                <div className="flex-1">
                  {review?.name && (
                    <h3 className="text-lg font-semibold text-white">
                      {review?.name}
                    </h3>
                  )}

                  {review?.rating && (
                    <span className="text-yellow-400 text-lg">
                      {
                        options.find(
                          (option) => option.value === String(review.rating),
                        )?.label
                      }
                    </span>
                  )}

                  {review?.productId?.title && (
                    <p className="mt-1 text-sm text-slate-400">
                      {review?.productId?.title}
                    </p>
                  )}

                  <div className="mt-4 flex items-center gap-3">
                    <input
                      id={`featured-${review?._id}`}
                      type="checkbox"
                      checked={review?.featured}
                      disabled={loadingId === review?._id}
                      onChange={(e) =>
                        handleFeatured(review?._id, e.target.checked)
                      }
                      className="h-4 w-4 rounded border-slate-600 bg-slate-800 accent-blue-600 disabled:opacity-50"
                    />

                    <label
                      htmlFor={`featured-${review?._id}`}
                      className="cursor-pointer text-sm text-slate-300"
                    >
                      Show on Homepage
                    </label>
                  </div>
                  <button
                    onClick={() =>
                      setOpenMessageId(
                        openMessageId === review?._id ? null : review?._id,
                      )
                    }
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-400 transition hover:bg-slate-800 hover:text-white mt-2"
                  >
                    <span>
                      {openMessageId === review?._id
                        ? "Show Less"
                        : "Show More"}
                    </span>

                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${
                        openMessageId === review?._id ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {/* Right Section */}
                <div className="flex flex-wrap items-center gap-2 md:justify-end">
                  {review.status === "pending" ? (
                    <button
                      disabled={loadingId === review?._id}
                      onClick={() => handleResolve(review?._id)}
                      className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loadingId === review?._id
                        ? "Approving..."
                        : "Mark Approved"}
                    </button>
                  ) : (
                    <span className="rounded-md bg-green-700 px-4 py-2 text-sm font-medium text-white">
                      ✓ Approved
                    </span>
                  )}

                  <button
                    disabled={loadingId === review?._id}
                    onClick={() => handleDelete(review?._id)}
                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loadingId === review?._id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
              {openMessageId === review?._id && (
                <div className="mt-5 border-t border-slate-800 pt-5 animate-in fade-in duration-300">
                  <div className="text-justify">
                    <h4 className="mb-2 text-sm font-semibold text-slate-300">
                      Customer Review
                    </h4>
                    {review?.review && (
                      <p className="leading-7 text-slate-400">
                        {review?.review}
                      </p>
                    )}
                  </div>
                  {review.photos?.length > 0 && (
                    <div className="mt-5">
                      <h4 className="mb-3 text-sm font-semibold text-slate-300 ">
                        Uploaded Photos
                      </h4>
                      <div className="flex flex-wrap gap-4">
                        {review?.photos.slice(0, 2).map((photo, index) => (
                          <img
                            key={index}
                            src={GetImageUrl(photo)}
                            alt={`Review ${index + 1}`}
                            className="h-36 w-36 rounded-lg border border-slate-700 object-cover"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
